import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api.js';
import { useSession } from '../../lib/useSession.js';
import { EnvBadge, type StackSummary } from '../stacks/StacksPage.js';
import '../stacks/stacks.css';
import '../deployments/deployments.css';
import './dashboard.css';

interface DeploymentRow {
  id: string;
  gitRef: string;
  gitSha: string;
  kind: string;
  status: string;
  createdAt: string;
}

export function DashboardPage() {
  const { data: me } = useSession();
  const navigate = useNavigate();
  const { data: stacks } = useQuery<StackSummary[]>({
    queryKey: ['stacks'],
    queryFn: () => apiFetch('/stacks'),
    refetchInterval: 15_000,
  });
  const { data: deployments } = useQuery<DeploymentRow[]>({
    queryKey: ['deployments', { limit: 8 }],
    queryFn: () => apiFetch('/deployments?limit=8'),
    refetchInterval: 15_000,
  });

  const envCount = (stacks ?? []).reduce((n, s) => n + s.environments.length, 0);
  const dirty = (stacks ?? []).flatMap((s) =>
    s.environments.filter((e) => e.status === 'dirty' || e.status === 'unknown').map((e) => ({ stack: s, env: e })),
  );

  return (
    <div>
      <header className="page-header">
        <div>
          <p className="page-eyebrow">Base Camp</p>
          <h1>Welcome back{me ? `, ${me.displayName}` : ''}</h1>
        </div>
      </header>

      <div className="dash-stats">
        <div className="card dash-stat">
          <span className="dash-stat-num">{stacks?.length ?? '—'}</span>
          <span className="dash-stat-label">stacks</span>
        </div>
        <div className="card dash-stat">
          <span className="dash-stat-num">{envCount || '—'}</span>
          <span className="dash-stat-label">environments</span>
        </div>
        <div className={`card dash-stat ${dirty.length ? 'dash-stat-warn' : ''}`}>
          <span className="dash-stat-num">{dirty.length}</span>
          <span className="dash-stat-label">need attention</span>
        </div>
      </div>

      {dirty.length > 0 ? (
        <div className="card dash-attention">
          <h3>Needs attention</h3>
          {dirty.map(({ stack, env }) => (
            <Link key={env.id} to={`/stacks/${stack.id}`} className="dash-attention-row">
              <span className="mono">
                {stack.slug}/{env.name}
              </span>
              <span className="env-status status-err">{env.status}</span>
            </Link>
          ))}
        </div>
      ) : null}

      <div className="dash-columns">
        <section>
          <div className="dash-section-head">
            <h2>Stacks</h2>
            <Link to="/stacks" className="dash-more">
              view all →
            </Link>
          </div>
          {(stacks ?? []).slice(0, 4).map((stack) => (
            <Link key={stack.id} to={`/stacks/${stack.id}`} className="card dash-stack">
              <div className="dash-stack-head">
                <span>{stack.name}</span>
                <span className="mono dim">{stack.slug}</span>
              </div>
              <div className="stack-envs">
                {stack.environments.map((env) => (
                  <EnvBadge key={env.id} env={env} />
                ))}
              </div>
            </Link>
          ))}
          {stacks && stacks.length === 0 ? (
            <div className="card dash-empty">
              <p>No stacks yet.</p>
              <Link to="/stacks" className="btn btn-amber">
                Create your first stack
              </Link>
            </div>
          ) : null}
        </section>

        <section>
          <div className="dash-section-head">
            <h2>Recent deployments</h2>
            <Link to="/deployments" className="dash-more">
              view all →
            </Link>
          </div>
          <div className="card dash-deploys">
            {(deployments ?? []).map((d) => (
              <button key={d.id} className="dash-deploy-row" onClick={() => navigate(`/deployments/${d.id}`)}>
                <span className="mono">{d.gitSha.slice(0, 7)}</span>
                <span className="mono dim dash-deploy-ref">{d.gitRef}</span>
                <span className="dim">{new Date(d.createdAt).toLocaleString()}</span>
                <span className={`deploy-status deploy-status-${d.status}`}>{d.status}</span>
              </button>
            ))}
            {deployments && deployments.length === 0 ? <p className="dim dash-deploys-empty">No deployments yet.</p> : null}
          </div>
        </section>
      </div>
    </div>
  );
}
